import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Heart, Phone, Check, ArrowLeft, X, Loader2, Clock } from 'lucide-react'
import { authApi, hospitalsApi } from '../../lib/api'
import { usePageTitle } from '../../hooks/usePageTitle'

const CROWD_TYPES = ['慢性病患者', '慢性病高危人群', '普通人群']
const AGE_GROUPS = ['>65', '<=65']

const UserRegister = () => {
  const navigate = useNavigate()
  usePageTitle()

  const [step, setStep] = useState(1)
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [showAgreementModal, setShowAgreementModal] = useState(false)
  const [agreementType, setAgreementType] = useState<'user' | 'privacy'>('user')
  const [formData, setFormData] = useState({
    name: '',
    idCard: '',
    gender: '',
    hospital_id: '',
    crowd_type: '',
    age_group: ''
  })

  // 机构列表
  const [hospitals, setHospitals] = useState<{ id: string; name: string }[]>([])
  const [loadingHospitals, setLoadingHospitals] = useState(false)

  const loadHospitals = async () => {
    setLoadingHospitals(true)
    try {
      const res: any = await hospitalsApi.list({ is_active: true })
      setHospitals((res.hospitals || res || []).map((h: any) => ({ id: h.id, name: h.name })))
    } catch {} finally {
      setLoadingHospitals(false)
    }
  }

  useEffect(() => {
    if (step === 3) {
      loadHospitals()
    }
  }, [step])

  const sendCode = async () => {
    if (!phone) {
      alert('请输入手机号')
      return
    }
    try {
      await authApi.sendCode(phone, 'register')
      setCountdown(60)
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '发送失败'
      alert(msg)
    }
  }

  const handleNext = () => {
    if (step === 1) {
      if (!phone || !code) {
        alert('请填写手机号和验证码')
        return
      }
      if (!agreed) {
        alert('请同意用户协议')
        return
      }
      setStep(2)
    } else if (step === 2) {
      if (!password || !confirmPassword) {
        alert('请填写密码')
        return
      }
      if (password !== confirmPassword) {
        alert('两次密码不一致')
        return
      }
      setStep(3)
    } else if (step === 3) {
      if (!formData.name || !formData.idCard) {
        alert('请填写姓名和身份证号')
        return
      }
      if (!formData.hospital_id) {
        alert('请选择绑定机构')
        return
      }
      if (!formData.age_group) {
        alert('请选择年龄范围')
        return
      }
      // 注册提交
      setLoading(true)
      authApi.register({
        phone,
        code,
        password,
        name: formData.name,
        id_card: formData.idCard,
        hospital_id: formData.hospital_id,
        crowd_type: formData.crowd_type || undefined,
        age_group: formData.age_group,
      }).then(() => {
        navigate('/user/home')
      }).catch((e: any) => {
        alert(e?.message || e?.detail || '注册失败')
        setLoading(false)
      })
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* 顶部导航 */}
      <div className="bg-green-700 text-white px-4 py-4 flex items-center">
        {step > 1 && (
          <button onClick={() => setStep(step - 1)} className="p-2 -ml-2">
            <ArrowLeft className="w-6 h-6" />
          </button>
        )}
        <h1 className="flex-1 text-center text-lg font-bold pr-8">
          {step === 1 ? '注册账号' : step === 2 ? '设置密码' : '完善信息'}
        </h1>
      </div>

      {/* 步骤指示 */}
      <div className="bg-white px-6 py-4">
        <div className="flex items-center justify-center space-x-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                  s < step
                    ? 'bg-green-600 text-white'
                    : s === step
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-12 h-0.5 mx-2 ${
                    s < step ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-center space-x-8 mt-2 text-xs">
          <span className={step >= 1 ? 'text-green-600' : 'text-gray-400'}>验证手机</span>
          <span className={step >= 2 ? 'text-green-600' : 'text-gray-400'}>设置密码</span>
          <span className={step >= 3 ? 'text-green-600' : 'text-gray-400'}>身份验证</span>
        </div>
      </div>

      {/* 表单内容 */}
      <div className="flex-1 px-6 py-6">
        {/* 步骤1: 手机验证 */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                手机号
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  placeholder="请输入手机号"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                验证码
              </label>
              <div className="flex space-x-3">
                <input
                  type="text"
                  placeholder="请输入验证码"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  onClick={sendCode}
                  disabled={countdown > 0}
                  className={`px-4 py-4 rounded-xl font-medium whitespace-nowrap ${
                    countdown > 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-green-700 text-white hover:bg-green-800'
                  }`}
                >
                  {countdown > 0 ? `${countdown}秒` : '获取验证码'}
                </button>
              </div>
            </div>

            <label className="flex items-start mt-4">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="w-4 h-4 mt-1 text-green-600 rounded focus:ring-green-500"
              />
              <span className="ml-2 text-sm text-gray-600">
                我已阅读并同意
                <button
                  type="button"
                  onClick={() => { setAgreementType('user'); setShowAgreementModal(true) }}
                  className="text-green-600"
                >《用户协议》</button>
                和
                <button
                  type="button"
                  onClick={() => { setAgreementType('privacy'); setShowAgreementModal(true) }}
                  className="text-green-600"
                >《隐私政策》</button>
              </span>
            </label>
          </div>
        )}

        {/* 步骤2: 设置密码 */}
        {step === 2 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                设置密码
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请设置登录密码（6-20位）"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-4 pr-12 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                确认密码
              </label>
              <input
                type="password"
                placeholder="请再次输入密码"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mt-4">
              <p className="text-sm text-gray-600 mb-2">密码要求:</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li className="flex items-center">
                  <Check className={`w-4 h-4 mr-2 ${password.length >= 6 ? 'text-green-600' : 'text-gray-300'}`} />
                  6-20位字符
                </li>
                <li className="flex items-center">
                  <Check className={`w-4 h-4 mr-2 ${/[a-zA-Z]/.test(password) ? 'text-green-600' : 'text-gray-300'}`} />
                  包含字母
                </li>
                <li className="flex items-center">
                  <Check className={`w-4 h-4 mr-2 ${/\d/.test(password) ? 'text-green-600' : 'text-gray-300'}`} />
                  包含数字
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* 步骤3: 身份验证 + 健康信息 */}
        {step === 3 && (
          <div className="space-y-4">
            {/* 姓名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">真实姓名</label>
              <input
                type="text"
                placeholder="请输入真实姓名"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* 身份证号 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">身份证号</label>
              <input
                type="text"
                placeholder="请输入18位身份证号"
                value={formData.idCard}
                onChange={(e) => setFormData({ ...formData, idCard: e.target.value.replace(/\D/g, '').slice(0, 18) })}
                className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
                maxLength={18}
              />
            </div>

            {/* 绑定机构 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">绑定机构 <span className="text-red-500">*</span></label>
              {loadingHospitals ? (
                <div className="py-4 text-center text-gray-400 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> 加载中...
                </div>
              ) : (
                <select
                  value={formData.hospital_id}
                  onChange={e => setFormData({ ...formData, hospital_id: e.target.value })}
                  className="w-full pl-4 pr-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                >
                  <option value="">请选择机构</option>
                  {hospitals.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* 疾病类型 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">人群类型</label>
              <div className="grid grid-cols-3 gap-2">
                {CROWD_TYPES.map(ct => (
                  <button
                    key={ct}
                    type="button"
                    onClick={() => setFormData({ ...formData, crowd_type: formData.crowd_type === ct ? '' : ct })}
                    className={`py-2 px-3 text-sm rounded-xl border-2 transition-colors ${
                      formData.crowd_type === ct
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:border-green-400'
                    }`}
                  >
                    {ct}
                  </button>
                ))}
              </div>
            </div>

            {/* 年龄范围 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">年龄范围 <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-2 gap-3">
                {AGE_GROUPS.map(ag => (
                  <button
                    key={ag}
                    type="button"
                    onClick={() => setFormData({ ...formData, age_group: ag })}
                    className={`py-3 px-4 text-sm rounded-xl border-2 transition-colors flex items-center justify-center gap-2 ${
                      formData.age_group === ag
                        ? 'border-green-600 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:border-green-400'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    {ag === '>65' ? '65岁以上' : '65岁及以下'}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-green-50 rounded-xl p-4 mt-4">
              <div className="flex items-start gap-2">
                <Heart className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">
                  <strong>温馨提示：</strong>机构绑定后将与该机构进行健康档案关联，机构信息可随时修改。
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 下一步按钮 */}
        <button
          onClick={handleNext}
          disabled={loading}
          className={`w-full py-4 rounded-xl font-medium mt-6 transition-colors flex items-center justify-center gap-2 ${
            loading
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-700 text-white hover:bg-green-800'
          }`}
        >
          {loading && <Loader2 className="w-5 h-5 animate-spin" />}
          {loading ? '注册中...' : step === 3 ? '完成注册' : '下一步'}
        </button>

        {/* 已有账号 */}
        {step === 1 && (
          <p className="text-center mt-6 text-gray-600">
            已有账号?
            <Link to="/user/login" className="text-green-600 font-medium ml-1">
              立即登录
            </Link>
          </p>
        )}
      </div>

      {/* 用户协议/隐私协议弹窗 */}
      {showAgreementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-bold text-white">
                {agreementType === 'user' ? '用户协议' : '隐私协议'}
              </h3>
              <button
                onClick={() => setShowAgreementModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {agreementType === 'user' ? (
                <div className="text-sm text-gray-700 space-y-3">
                  <p><strong>眉山疾控中心积分系统用户协议</strong></p>
                  <p>欢迎您使用眉山疾控中心积分系统！在使用本系统前，请仔细阅读以下协议：</p>
                  <p><strong>一、服务内容</strong></p>
                  <p>本系统为眉山疾控中心提供的健康管理积分服务平台，用户可通过参与健康体检、公共卫生服务等活动获取积分，积分可用于兑换健康礼品。</p>
                  <p><strong>二、用户注册</strong></p>
                  <p>1. 用户需使用真实身份信息注册，确保信息准确无误。</p>
                  <p>2. 每位用户仅可注册一个账号，不得出借、转让。</p>
                  <p>3. 用户应妥善保管账号密码，因密码泄露造成的损失由用户自行承担。</p>
                  <p><strong>三、积分规则</strong></p>
                  <p>1. 积分通过参与系统认可的健康活动获取。</p>
                  <p>2. 积分不可转让、不可兑换现金。</p>
                  <p>3. 积分有效期为获取之日起一年内有效。</p>
                  <p><strong>四、用户行为规范</strong></p>
                  <p>1. 用户不得利用系统进行任何违法违规活动。</p>
                  <p>2. 用户不得通过虚假手段获取积分。</p>
                  <p>3. 用户应遵守系统各项规则和管理规定。</p>
                  <p><strong>五、免责声明</strong></p>
                  <p>1. 因不可抗力导致的服务中断，本系统不承担责任。</p>
                  <p>2. 本系统保留对协议的最终解释权和修改权。</p>
                </div>
              ) : (
                <div className="text-sm text-gray-700 space-y-3">
                  <p><strong>眉山疾控中心积分系统隐私协议</strong></p>
                  <p>我们高度重视用户隐私保护，本协议说明我们如何收集、使用和保护您的个人信息：</p>
                  <p><strong>一、信息收集</strong></p>
                  <p>我们收集的信息包括：姓名、手机号、身份证号、健康体检记录等。</p>
                  <p><strong>二、信息使用</strong></p>
                  <p>1. 用于身份验证和账号管理。</p>
                  <p>2. 用于积分计算和礼品兑换。</p>
                  <p>3. 用于健康管理和随访服务。</p>
                  <p>4. 用于统计分析和服务改进。</p>
                  <p><strong>三、信息保护</strong></p>
                  <p>1. 我们采用严格的安全措施保护您的个人信息。</p>
                  <p>2. 未经您的同意，我们不会向第三方披露您的个人信息。</p>
                  <p>3. 法律法规另有规定的除外。</p>
                  <p><strong>四、用户权利</strong></p>
                  <p>1. 您有权查询、更正自己的个人信息。</p>
                  <p>2. 您有权要求删除自己的个人信息。</p>
                  <p>3. 您有权撤回授权同意。</p>
                  <p><strong>五、联系我们</strong></p>
                  <p>如有任何隐私相关问题，请联系眉山疾控中心。</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex-shrink-0">
              <button
                onClick={() => setShowAgreementModal(false)}
                className="w-full py-3 bg-green-700 text-white rounded-xl font-medium hover:bg-green-800 transition-colors"
              >
                我已阅读
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserRegister